import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from "../config.js";
import { useToast } from '../context/ToastContext.js';

function AthleteRoutineTracker({ athleteId }) {
  const toast = useToast();
  const [selectedFile, setSelectedFile] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [videoId, setVideoId] = useState(null);

  // Classification form states
  const [landingQuality, setLandingQuality] = useState('Stick');
  const [entryType, setEntryType] = useState('Direct');
  const [classifyLoading, setClassifyLoading] = useState(false);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
    setError('');
  };

  const handleAnalyze = async () => {
    if (!athleteId) {
      setError('Athlete ID is missing.');
      return;
    }

    if (!selectedFile) {
      setError('Please select a video file.');
      return;
    }

    setLoading(true);
    setError('');
    setAnalysisResult(null);
    setVideoId(null);

    const formData = new FormData();
    formData.append('video', selectedFile);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/athletes/${athleteId}/landing`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${JSON.parse(localStorage.getItem('user'))?.token}`,
          },
        }
      );

      setAnalysisResult(response.data);
      setVideoId(response.data.landingId);
      // Reset classification form to defaults
      setLandingQuality('Stick');
      setEntryType('Direct');
    } catch (err) {
      console.error('❌ Upload error:', err);
      setError(err.response?.data?.error || 'Server connection failed.');
    }

    setLoading(false);
  };

  const handleSubmitClassification = async () => {
    if (!videoId) {
      setError('Video ID is missing.');
      return;
    }

    setClassifyLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/label-landing`,
        {
          landing_id: videoId,
          landing_type: landingQuality,
          entry: entryType,
        },
        {
          headers: {
            Authorization: `Bearer ${JSON.parse(localStorage.getItem('user'))?.token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success('Landing classification saved successfully!');
        // Reset form
        setSelectedFile(null);
        setAnalysisResult(null);
        setVideoId(null);
        setLandingQuality('Stick');
        setEntryType('Direct');
      }
    } catch (err) {
      console.error('❌ Classification error:', err);
      setError(err.response?.data?.error || 'Failed to save classification.');
    }

    setClassifyLoading(false);
  };

  return (
    <div style={{ padding: '2rem', fontFamily: 'Arial, sans-serif', maxWidth: '700px', margin: 'auto' }}>
      <h2>🎥 Landing Analysis & Classification</h2>

      <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f9f9f9', borderRadius: '8px' }}>
        <h3>Upload Video</h3>
        <input type="file" accept="video/*" onChange={handleFileChange} style={{ marginBottom: '1rem' }} />
        <br />
        <button 
          onClick={handleAnalyze} 
          disabled={loading}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Analyzing...' : 'Upload & Analyze'}
        </button>
      </div>

      {error && <p style={{ color: 'red', marginTop: '1rem', padding: '1rem', background: '#ffe6e6', borderRadius: '4px' }}>{error}</p>}

      {analysisResult && (
        <div style={{ marginTop: '2rem' }}>
          <div style={{ padding: '1.5rem', background: '#e7f3ff', borderRadius: '8px', marginBottom: '2rem' }}>
            <h3>AI Analysis Results</h3>
            <pre
              style={{
                background: '#f4f4f4',
                padding: '1rem',
                borderRadius: '4px',
                overflowX: 'auto',
                fontSize: '0.9rem',
              }}
            >
              {JSON.stringify(analysisResult, null, 2)}
            </pre>
          </div>

          <div style={{ padding: '1.5rem', background: '#f0f9ff', borderRadius: '8px' }}>
            <h3>📋 Manual Classification</h3>
            <p style={{ marginBottom: '1.5rem', color: '#555' }}>
              Review and adjust the landing classification before saving:
            </p>

            {/* Landing Quality */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Landing Quality:
              </label>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {['Stick', 'Hop/Step', 'Over-rotated', 'Short Landing'].map((option) => (
                  <label key={option} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value={option}
                      checked={landingQuality === option}
                      onChange={(e) => setLandingQuality(e.target.value)}
                      style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                    />
                    {option}
                  </label>
                ))}
              </div>
            </div>

            {/* Entry Type */}
            <div style={{ marginBottom: '2rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Entry Type:
              </label>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {['Direct', 'Piked', 'Layouts', 'Arabesque'].map((option) => (
                  <label key={option} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value={option}
                      checked={entryType === option}
                      onChange={(e) => setEntryType(e.target.value)}
                      style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                    />
                    {option}
                  </label>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={handleSubmitClassification}
                disabled={classifyLoading}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: classifyLoading ? 'not-allowed' : 'pointer',
                  opacity: classifyLoading ? 0.6 : 1,
                  fontSize: '1rem',
                }}
              >
                {classifyLoading ? 'Saving...' : '✅ Save Classification'}
              </button>
              <button
                onClick={() => {
                  setAnalysisResult(null);
                  setVideoId(null);
                  setSelectedFile(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AthleteRoutineTracker;
